import { useRef, useCallback } from 'react';

/**
 * Minimum amount of shift (in pixels) required before applying a correction
 */
var TEXT_INK_SHIFT_THRESHOLD = 0.7;
/**
 * Multiplier to adjust the shift amount for a more conservative correction
 */
var TEXT_INK_SHIFT_MULTIPLIER = 0.5;
/**
 * Calculates how much to shift text vertically to make it appear truly centered
 *
 * THE PROBLEM:
 * When we center text with CSS, the browser centers the font's invisible box,
 * not the actual visible characters. Many fonts have more empty space above
 * or below the text, causing them to look off-center even when they're
 * technically centered.
 *
 * ```
 *            What the browser centers:
 *         ┌───────────────────────────┐
 *         │    Font's Invisible Box   │
 *         │                           │
 *         │        ┌─────────┐        │ ← Extra space above text
 *         │        │  Hello  │        │   makes it appear too low
 *         │        └─────────┘        │
 *         │                           │ ← Less space below text
 *         └───────────────────────────┘
 *                    ↓
 *            What we want to center:
 *               ┌─────────┐
 *               │  Hello  │
 *               └─────────┘
 * ```
 *
 * This function measures how much extra space exists above vs. below the actual
 * visible text and calculates the shift needed to make it look centered.
 * A completely centered text will have the same amount of empty space above and below.
 * But we apply a threshold to prevent micro-adjustments for fonts with minimal asymmetry.
 *
 * @param ctx - Canvas context with font already applied
 * @param text - The text to measure
 * @param threshold - Minimum shift needed to apply correction
 * @param multiplier - Multiplier to adjust the shift amount
 * @returns The vertical shift in pixels (positive = shift up, negative = shift down)
 */
var measureFontShift = function (ctx, text, threshold, multiplier) {
    var textMetrics = ctx.measureText(text);
    // Measure empty space at the top: (fontBox.top - actualText.top)
    var topTextBoundingBoxDifference = textMetrics.fontBoundingBoxAscent - textMetrics.actualBoundingBoxAscent;
    // Measure empty space at the bottom: (fontBox.bottom - actualText.bottom)
    var bottomTextBoundingBoxDifference = textMetrics.fontBoundingBoxDescent - textMetrics.actualBoundingBoxDescent;
    // Calculate the difference between these spaces (a value close to 0 means the text is centered)
    var shift = (topTextBoundingBoxDifference - bottomTextBoundingBoxDifference) * Math.abs(multiplier);
    if (Math.abs(shift) > threshold) {
        /**
         * Y axis grows downward in web browsers:
         *   0 ┌─→ x
         *     │
         *   1 │
         *     │
         *   2 │
         *     ↓ y
         *
         * So we negate the shift to get the correct direction:
         * - Positive shift (moves down) should become negative (moves up)
         * - Negative shift (moves up) should become positive (moves down)
         */
        return -shift;
    }
    return 0;
};
var createCanvas = function () {
    var canvas = document.createElement('canvas');
    var ctx = canvas.getContext('2d');
    if (!ctx) {
        return null;
    }
    var cleanup = function () { var _a; return (_a = canvas.remove) === null || _a === void 0 ? void 0 : _a.call(canvas); };
    return { canvas: canvas, ctx: ctx, cleanup: cleanup };
};
/**
 * Calculate the font shift needed to center font glyphs within their bounding box
 *
 * @param element - HTML element to evaluate
 * @param ctx - Canvas context with font already applied
 * @param threshold - Minimum shift needed to apply correction (default: 0.7px)
 * @param multiplier - Multiplier to adjust the shift amount (default: 0.5)
 * @returns Object with element and its font shift amount
 */
var calculateElementFontShift = function (element, ctx, threshold, multiplier) {
    if (threshold === void 0) { threshold = TEXT_INK_SHIFT_THRESHOLD; }
    if (multiplier === void 0) { multiplier = TEXT_INK_SHIFT_MULTIPLIER; }
    try {
        var text = element.textContent;
        if (!text) {
            // ignore empty text
            return undefined;
        }
        // load the font into the canvas to measure the text
        var style = getComputedStyle(element);
        ctx.font = style.font;
        var fontShift = measureFontShift(ctx, text, threshold, multiplier);
        if (fontShift) {
            return {
                element: element,
                fontShift: fontShift,
            };
        }
        // ignore text that doesn't need shifting
        return undefined;
    }
    catch (e) {
        console.warn('Failed to calculate font shift to center text:', e);
        return undefined;
    }
};
/**
 * Hook that returns a function for correcting font glyph optical centering issues.
 *
 * This hook solves a problem that CSS alignments (flex, grid, etc.) cannot fix:
 * the optical illusion created by font designs where the actual ink/glyphs have
 * uneven vertical distribution within their bounding box. No amount of CSS
 * container centering can fix this, as it's an issue with the font itself.
 *
 * ```
 * CSS centers the container perfectly, but the font's ink is off-center:
 *
 * ┌───────────────────┐            ┌───────────────────┐
 * │ ┌───────────────┐ │            │ ┌───────────────┐ │
 * │ │               │ │            │ │               │ │
 * │ │               │ │ ← CSS      │ │     Text      │ │ ← Hook adjusts
 * │ │     Text      │ │   centers  │ │  (corrected)  │ │   the text
 * │ │  (off-center) │ │   box      │ │               │ │   position
 * │ └───────────────┘ │            │ └───────────────┘ │
 * └───────────────────┘            └───────────────────┘
 *     Container centered             Container centered
 *     Font ink is not                Font ink appears centered
 * ```
 *
 * @returns Function to apply optical font centering to one or more HTML elements
 *
 */
var useOpticalFontCenter = function () {
    var processedElementsRef = useRef([]);
    var validateProcessedElements = function (elements) {
        elements.forEach(function (element) {
            var processedElement = processedElementsRef.current.find(function (proccessedElement) { return proccessedElement === element; });
            if (processedElement && processedElement.style.font !== element.style.font) {
                processedElementsRef.current = processedElementsRef.current.filter(function (el) { return el !== processedElement; });
                return;
            }
        });
    };
    return useCallback(function () {
        var elements = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            elements[_i] = arguments[_i];
        }
        var createdCanvas = createCanvas();
        if (!createdCanvas) {
            // failed to create canvas
            console.warn('Failed to create canvas for optical font centering');
            return;
        }
        var ctx = createdCanvas.ctx, cleanup = createdCanvas.cleanup;
        validateProcessedElements(elements);
        elements
            .filter(function (el) { return !processedElementsRef.current.includes(el); })
            .forEach(function (element) {
            var _a;
            var currentTransform = element.style.transform;
            if (currentTransform === null || currentTransform === void 0 ? void 0 : currentTransform.includes('translateY')) {
                // assume if element already has a translateY transform, we don't need to adjust it
                processedElementsRef.current.push(element);
                return;
            }
            var fontShift = ((_a = calculateElementFontShift(element, ctx)) !== null && _a !== void 0 ? _a : {}).fontShift;
            if (fontShift) {
                // apply the shift to the element preserving existing transforms
                element.style.transform = currentTransform
                    ? "".concat(currentTransform, " translateY(").concat(fontShift, "px)")
                    : "translateY(".concat(fontShift, "px)");
            }
            processedElementsRef.current.push(element);
        });
        cleanup();
    }, []);
};

export { calculateElementFontShift, useOpticalFontCenter };
//# sourceMappingURL=opticalFontCenter.js.map
